/**
 * Slots for the order panels built in web/src/account/orders/ (another ticket). The components are picked
 * up automatically when they exist: any module under ./orders/ exporting `OpenOrdersPanel`,
 * `OrderHistoryPanel` or `FillsPanel` (props `{ address: string }`). Until then a placeholder renders.
 */
import type { ComponentType } from 'react';
import { useT } from '../i18n/useT';

type PanelProps = { address: string };
type PanelName = 'OpenOrdersPanel' | 'OrderHistoryPanel' | 'FillsPanel';

const modules = import.meta.glob<Record<string, unknown>>(['./orders/**/*.tsx', '!./orders/**/*.test.tsx'], {
  eager: true,
});

function findPanel(name: PanelName): ComponentType<PanelProps> | null {
  for (const mod of Object.values(modules)) {
    const c = mod[name];
    if (typeof c === 'function' || (typeof c === 'object' && c !== null)) return c as ComponentType<PanelProps>;
  }
  return null;
}

const OpenOrders = findPanel('OpenOrdersPanel');
const OrderHistory = findPanel('OrderHistoryPanel');
const Fills = findPanel('FillsPanel');

function Pending({ title }: { title: string }) {
  const t = useT();
  return (
    <div className="card p-6 text-sm text-muted" data-testid="panel-placeholder">
      {t('portfolio.pending', { panel: title })}
    </div>
  );
}

function PendingTab({ k }: { k: 'portfolio.tab.orders' | 'portfolio.tab.history' | 'portfolio.tab.trades' }) {
  const t = useT();
  return <Pending title={t(k)} />;
}

export function OpenOrdersSlot({ address }: PanelProps) {
  return OpenOrders ? <OpenOrders address={address} /> : <PendingTab k="portfolio.tab.orders" />;
}

export function OrderHistorySlot({ address }: PanelProps) {
  return OrderHistory ? <OrderHistory address={address} /> : <PendingTab k="portfolio.tab.history" />;
}

export function TradesSlot({ address }: PanelProps) {
  return Fills ? <Fills address={address} /> : <PendingTab k="portfolio.tab.trades" />;
}
